import React, { useState, useEffect } from "react";
import { Fab, Button, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import "./ReportDownloadModal.scss";
import DownloadIcon from "@mui/icons-material/Download";
import urls from "config/urls";
import AxiosInterceptor from "../../../AxiosInterceptor";
import Modal from "components/Modal/Modal";
import { Toast } from "components/Toast/Toast";
import Icon from "components/Icon/Icon";
import { DynamicForm } from "components/DynamicForm/DynamicForm";

interface Field {
    name: string;
    label: string;
    type: string;
    required?: boolean;
    options?: any[];
    defaultValue?: any;
}

interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    downloadPath: string;
    formats?: string[]; // ['pdf', 'excel', 'csv'] - formatos disponibles
    defaultFormat?: string; // Formato preseleccionado
    title?: string; // Título del modal
    fields?: Field[]; // Campos para el formulario (usados por DynamicForm)
}

const ReportDownloadModal: React.FC<ModalProps> = ({
    isOpen,
    onClose,
    downloadPath,
    formats = ['pdf', 'excel', 'csv'],
    defaultFormat = 'pdf',
    title = "Descargar Reporte",
    fields = []
}) => {
    const [selectedFormat, setSelectedFormat] = useState(defaultFormat);
    const [formData, setFormData] = useState<any>({});
    const [processing, setProcessing] = useState(false);
    const [toastIsOpen, setToastIsOpen] = useState(false);
    const [toastMessage, setToastMessage] = useState("");
    const [toastSeverity, setToastSeverity] = useState("error");

    const handleFormatChange = (event: any) => {
        setSelectedFormat(event.target.value);
    };

    const handleFormDataChange = (data: any) => {
        setFormData(data);
    };

    const onDownload = async () => {
        setProcessing(true);
        try {
            const url = `${urls.server}/api/${downloadPath}`;
            const payload = {
                format: selectedFormat,
                ...formData
            };

            const response: any = await AxiosInterceptor.post(url, payload, {
                responseType: 'blob'
            });

            // Crear y descargar el archivo
            const blob = new Blob([response.data]);
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = `reporte.${selectedFormat}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);

            setProcessing(false);
            setToastSeverity("success");
            setToastMessage("Reporte descargado exitosamente");
            setToastIsOpen(true);
            onClose();
        } catch (error: any) {
            setProcessing(false);
            setToastMessage("Error al descargar el reporte: " + error.toString());
            setToastIsOpen(true);
            setToastSeverity("error");
        }
    };

    useEffect(() => {
        if (!isOpen) {
            setSelectedFormat(defaultFormat);
            setFormData({});
            setProcessing(false);
        }
    }, [isOpen, defaultFormat]);

    const handleOnClose = () => {
        onClose();
    };

    const getFormatLabel = (format: string) => {
        const formatLabels: { [key: string]: string } = {
            'pdf': 'PDF',
            'excel': 'Excel',
            'csv': 'CSV'
        };
        return formatLabels[format] || format.toUpperCase();
    };

    return (
        <>
            <Modal
                title={title}
                subtitle="Configurar y descargar reporte"
                className={'report-download-modal-container'}
                isOpen={isOpen}
                onClose={handleOnClose}
            >
                <div className="download-config-wrapper">
                    {/* Selector de formato */}
                    <FormControl fullWidth className="format-selector">
                        <InputLabel>Formato de descarga</InputLabel>
                        <Select
                            value={selectedFormat}
                            label="Formato de descarga"
                            onChange={handleFormatChange}
                        >
                            {formats.map((format) => (
                                <MenuItem key={format} value={format}>
                                    {getFormatLabel(format)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Formulario dinámico si hay campos */}
                    {fields.length > 0 && (
                        <div className="form-section">
                            <h4>Configuración del reporte</h4>
                            <DynamicForm
                                fields={fields}
                                data={formData}
                                onChange={handleFormDataChange}
                            />
                        </div>
                    )}
                </div>

                <Fab
                    className="fab-btn"
                    color="primary"
                    aria-label="download"
                    disabled={processing}
                    onClick={onDownload}
                >
                    {processing ? <Icon name="loading" /> : <DownloadIcon />}
                </Fab>
            </Modal>
            <Toast
                isOpen={toastIsOpen}
                onClose={() => setToastIsOpen(false)}
                message={toastMessage}
                severity={toastSeverity}
            />
        </>
    );
};

export { ReportDownloadModal };
